import {
  PosterType,
  BreadcrumbItemType,
  TrendingListResponseType,
  BlogPostResponseType,
  BlogAuthorResponseType,
  FAQsResponseType,
  HowToSchemaResponseType,
  VideoSchemaResponseType,
  FeatureImage,
} from '../types';
import { BrowserType } from '../constants/common';
import { PlatformType } from '@invideoio/web-shared/constants/common';
import { LOCAL_STORAGE } from '@invideoio/web-shared/utils/utils.constant';

export const getPosterURL = (poster: PosterType, browser: string) => {
  if (!poster) {
    return;
  }
  return browser !== BrowserType.Safari && poster.webpURL
    ? poster.webpURL
    : poster.defaultURL;
};

export const getWebpURL = (url: string, platform = PlatformType.Desktop) => {
  const webpURL = url.slice(0, url.lastIndexOf('.')) + '.webp';
  const imageSizeText =
    platform === PlatformType.Desktop ? '/large/' : '/medium/';
  return webpURL.replace('/origin/', imageSizeText);
};

export const getAssetUrl = (type: string, assetAddress: string) => {
  const assetTypeUrlMap: { [key: string]: string } = {
    blog: `${process.env.NEXT_PUBLIC_BLOG_CDN_URL}`,
    common: `${process.env.NEXT_PUBLIC_SHARED_CDN_URL}`,
  };
  return `${assetTypeUrlMap[type]}${assetAddress}`;
};

export const deSlugify = (slug: string) => {
  return slug
    .split('-')
    .map(word => word.toLowerCase())
    .join(' ');
};

export const generateBreadcrumbsFromUrl = (url: string) => {
  const crumbs: BreadcrumbItemType[] = [];
  let list = url.split('/');

  if (list.length > 3 && list[1].length === 2) {
    list.splice(1, 1);
  }

  list.forEach((item, index) => {
    let crumb = {} as BreadcrumbItemType;
    if (index == 0) {
      crumb.name = 'invideo.io';
      crumb.title = 'Go to Academy for Professional Quality Video Creation.';
    } else {
      crumb.name = deSlugify(item);
      if (crumb.name === 'blog') {
        crumb.title = 'Go to Academy for Professional Quality Video Creation.';
      } else {
        crumb.title = crumb.name;
      }
    }
    let link = '';
    for (let i = 0; i <= index; i++) {
      link += `${list[i]}/`;
    }

    if (index == 0) {
      crumb.url = process.env.NEXT_PUBLIC_BASE_HOST || 'https://invideo.io';
    } else {
      crumb.url = link;
    }
    crumbs.push(crumb);
  });

  return crumbs;
};

export const getProfessionBackgroundColor = (slug: string) => {
  const bgColorMap: { [key: string]: string } = {
    fitness: 'iv-bg-[#F2D1D3]',
    'real-estate': 'iv-bg-[#B5EFFB]',
  };
  return bgColorMap[slug] ?? 'iv-bg-grey-50';
};

export const createTrendingPostsFromRawPosts = (
  trendingPosts: TrendingListResponseType[],
) => {
  return trendingPosts.map(post => {
    return {
      id: post.id,
      title: post.Title,
      featureImage: post.FeatureImage,
      slug: post.Slug,
      trending: post.trending,
      category: post.categories_v_2,
      platform: post.platform,
      profession: post.profession,
    };
  });
};

export const getFormattedDate = (date: Date) => {
  const year = date.getFullYear();
  const month = date.toLocaleString('default', { month: 'long' });
  const day = date.getDate();

  return `${month} ${day < 10 ? '0' : ''}${day}, ${year}`;
};

export const calculateReadingTime = (content: string) => {
  const stripedHtml = content.replace(/<[^>]+>/g, '');
  const wordsPerMinute = 200;
  const words = stripedHtml.trim().split(/\s+/).length;
  const wordsTimeInMin = Math.ceil(words / wordsPerMinute);
  const timeInMin = Math.min(Math.max(wordsTimeInMin, 1), 15);
  return `${timeInMin} min`;
};

const findFirstChildAnchorId = (node: any) => {
  for (let child of node.children) {
    if (child.nodeName === 'A') {
      return child.id;
    }
  }
};

export const getTableOfContents = () => {
  let tableOfContents: any = [];

  return function getTableOfContents(parent: any) {
    if (parent) {
      for (let node of parent.children) {
        // Add node.nodeName !== H4 in following condition to include it as subheading.
        if (node.nodeName !== 'H2') continue;

        if (node.nodeName === 'H2') {
          const anchorId = findFirstChildAnchorId(node);

          const id = node.id || anchorId;
          if (id) {
            tableOfContents.push({
              id: node.id || anchorId,
              text: node.innerText,
              children: [],
            });
          }
        } else if (node.nodeName === 'H4') {
          if (tableOfContents[tableOfContents.length - 1]) {
            const anchorId = findFirstChildAnchorId(node);
            const id = node.id || anchorId;
            if (id) {
              tableOfContents[tableOfContents.length - 1].children.push({
                id: node.id || anchorId,
                text: node.innerText,
              });
            }
          }
        }

        getTableOfContents(node);
      }
    }
    return tableOfContents;
  };
};

export const createElementFromHTML = (htmlString: string) => {
  if (htmlString && typeof window !== 'undefined') {
    const div = document.createElement('div');
    div.innerHTML = htmlString.trim();
    return div;
  }
};
export const generateTOC = (htmlString: string) => {
  const element = createElementFromHTML(htmlString);
  const generatetoc = getTableOfContents();
  return generatetoc(element);
};

export const createPostsfromRawPosts = (posts: BlogPostResponseType) => {
  const {
    id,
    Slug: slug,
    Title: title,
    published_at: publishedAt,
    created_at: createdAt,
    updated_at: updatedAt,
    BlogBody: blogBody,
    Language: language,
    author,
    Excerpt: excerpt,
    ShowFeatureImageInHead: showFeatureImageInHead,
    platform,
    profession,
    trending,
    categories_v_2: category,
    SEO: seo,
    FAQs: faqs,
    HowToSchema: howToSchema,
    VideoSchema: videoSchema,
    ContextualCard: contextualCard,
    FeatureImage: featureImage,
  } = posts;
  return {
    id,
    slug,
    title,
    publishedAt,
    createdAt,
    updatedAt,
    blogBody,
    language,
    author: author ? createAuthorFromRaw(author) : null,
    excerpt,
    showFeatureImageInHead,
    platform,
    profession,
    trending,
    category,
    seo,
    faqs: faqs ? createFAQsfromRaw(faqs) : null,
    SEOFAQ: faqs?.FAQ || null,
    howToSchema: howToSchema ? createHowToSchemafromRaw(howToSchema) : null,
    videoSchema: videoSchema ? createVideoSchemafromRaw(videoSchema) : null,
    contextualCard,
    featureImage,
  };
};

const createAuthorFromRaw = (author: BlogAuthorResponseType) => {
  if (author) {
    return {
      id: author.id,
      username: author.username,
      email: author.email,
      role: author.role,
      name: author.Name,
      slug: author.Slug,
      avatar: author.Avatar,
      firstName: author.FirstName,
      lastName: author.LastName,
      url: author.URL,
    };
  }
};

const createFAQsfromRaw = (FAQs: FAQsResponseType) => {
  if (FAQs) {
    const { id, Title: title, Subtitle: subtitle, FAQ: faq } = FAQs;
    return {
      id,
      title,
      subtitle,
      faq,
    };
  }
};

const createHowToSchemafromRaw = (howToSchema: HowToSchemaResponseType[]) => {
  if (howToSchema) {
    return howToSchema.map(data => {
      return {
        id: data.id,
        title: data.Title,
        description: data.Description,
        step: data.Step,
        image: data.Image,
      };
    });
  }
};

const createVideoSchemafromRaw = (videoSchema: VideoSchemaResponseType[]) => {
  if (videoSchema) {
    return videoSchema.map(schema => {
      return {
        id: schema.id,
        name: schema.Name,
        description: schema.Description,
        uploadDate: schema.UploadDate,
        embedUrl: schema.EmbedUrl,
        thumbnail: schema.Thumbnail,
      };
    });
  }
};

export const getPlatformHeaderBackground = (slug: string) => {
  const bgColorMap: Record<string, string> = {
    instagram: 'iv-instagram-header-bg',
    youtube: 'iv-youtube-header-bg',
    tiktok: 'iv-tiktok-header-bg',
    facebook: 'iv-facebook-header-bg',
    twitter: 'iv-twitter-header-bg',
    linkedin: 'iv-linkedin-header-bg',
  };
  return bgColorMap[slug] ?? 'iv-bg-grey-50';
};
export const getCategoryBackgroundColor = (slug: string) => {
  const bgColorMap: { [key: string]: string } = {
    'mobile-filmmaking-tips': 'iv-bg-[#D1F4F9]',
    'case-studies': 'iv-bg-[#FED7F0]',
    'new-video-ideas': 'iv-bg-[#C3C0FF]',
    'video-editing-tips': 'iv-bg-[#FFF4BF]',
    'social-media-marketing-tips': 'iv-bg-[#95B9FF]',
    'video-marketing-tips': 'iv-bg-[#CEF2F5]',
  };
  return bgColorMap[slug] ?? 'iv-bg-grey-7';
};

export const getOptimizeWebpUrl = (
  featureImage: FeatureImage,
  platform: PlatformType = PlatformType.Desktop,
  browser: BrowserType = BrowserType.Chrome,
) => {
  if (browser !== BrowserType.Safari) {
    return (
      featureImage?.[platform]?.webpURL ||
      featureImage?.[platform]?.defaultURL ||
      featureImage?.url
    );
  }
  return featureImage?.[platform]?.defaultURL || featureImage?.url;
};

export const isUserLoggedIn = () => {
  const authToken = localStorage.getItem(LOCAL_STORAGE.AUTH_TOKEN);
  return (
    authToken != null && authToken !== undefined && authToken?.trim() !== ''
  );
};
